import React, { ChangeEvent } from 'react';
import './Input.scss';

// Define types for input variants
type InputType = 'text' | 'email' | 'password' | 'number' | 'tel' | 'search' | 'url' | 'textarea';
type InputSize = 'small' | 'medium' | 'large';
type IconPosition = 'left' | 'right';
type Theme = 'light' | 'dark';

// Define component props interface
interface InputProps {
    type?: InputType;
    size?: InputSize;
    value?: string;
    defaultValue?: string;
    placeholder?: string;
    label?: string;
    name?: string;
    id?: string;
    onChange?: (event: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>) => void;
    onFocus?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    onBlur?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    icon?: React.ReactElement<any> | string;
    iconPosition?: IconPosition;
    className?: string;
    fullWidth?: boolean;
    resize?: 'none' | 'both' | 'horizontal' | 'vertical';
    rows?: number;
    cols?: number;
    maxLength?: number;
    theme?: Theme;
    helperText?: String;
    [key: string]: any;
}

const Input: React.FC<InputProps> = ({
    type = 'text',
    size = 'medium',
    value,
    defaultValue,
    placeholder,
    label,
    name,
    id,
    onChange,
    onFocus,
    onBlur,
    icon,
    iconPosition = 'right',
    className = '',
    fullWidth = false,
    resize = 'vertical',
    rows = 4,
    cols,
    maxLength,
    theme = 'dark',
    helperText = '',
    ...restProps
}) => {
    const inputId = id || name || `input-${Math.random().toString(36).substr(2, 9)}`;
    const isTextarea = type === 'textarea';

    // Construct class names
    const containerClass = `input-container ${fullWidth ? 'full-width' : ''} ${className}`;
    const inputWrapperClass = `input-wrapper ${icon ? `icon-${iconPosition}` : ''}`;
    const inputClass = `input-field ${size} ${theme}`;
    const textareaClass = `textarea-field ${size} ${theme} resize-${resize}`;

    return (
        <div className={containerClass}>
            {label && <label htmlFor={inputId} className="input-label">{label}</label>}
            <div className={inputWrapperClass}>
                {icon && (
                    typeof icon === 'string' ? (
                        <img
                            src={icon}
                            alt=""
                            className={`input-icon input-icon-${iconPosition}`}
                        />
                    ) : (
                        React.cloneElement(
                            icon,
                            {
                                className: `input-icon input-icon-${iconPosition} ${icon.props.className || ''}`
                            }
                        )
                    )
                )}
                {isTextarea ? (
                    <textarea
                        id={inputId}
                        name={name}
                        value={value}
                        defaultValue={defaultValue}
                        placeholder={placeholder}
                        onChange={onChange}
                        onFocus={onFocus}
                        onBlur={onBlur}
                        rows={rows}
                        cols={cols}
                        maxLength={maxLength}
                        className={textareaClass}
                        {...restProps}
                    />
                ) : (
                    <input
                        id={inputId}
                        type={type}
                        name={name}
                        value={value}
                        defaultValue={defaultValue}
                        placeholder={placeholder}
                        onChange={onChange}
                        onFocus={onFocus}
                        onBlur={onBlur}
                        className={inputClass}
                        {...restProps}
                    />
                )}
            </div>
            {helperText !== '' && <p style={{ color: 'red', fontSize: '1.5vh', fontWeight: '400' }}>{helperText}</p>}

        </div>
    );
};

export default Input;